import {
  CategoryData,
  ProductOptionsData,
} from "../components/ProductOption/ProductOption";
import { isCompatibleCategory, validOptions, SubCategoryMap } from "./options";

// Helper function to get list of selected category IDs
function getSelectedCategories(subcatmap: SubCategoryMap): string[] {
  return (subcatmap.selectedOptions ?? []).reduce<string[]>((pv, so) => {
    const catId = so.SubCategory?.Category?._id;
    if (!catId || pv.includes(catId)) {
      return pv;
    }
    return [catId, ...pv];
  }, []);
}

// Helper function to find incompatible categories
function findIncompatibleCategories(
  categoryId: string,
  subcatmap: SubCategoryMap,
  categorys: CategoryData[]
): string[] {
  const category = categorys.find((c) => c._id === categoryId);
  if (!category) return [];

  const selectedCategories = getSelectedCategories(subcatmap);
  const incompatibleCategories: string[] = [];

  // Check if any selected categories are incompatible with our category
  for (const selectedId of selectedCategories) {
    const selectedCategory = categorys.find((c) => c._id === selectedId);
    if (!selectedCategory) continue;

    // Check both directions of incompatibility
    if (
      selectedCategory.incompatibleCategorys?.includes(categoryId) ||
      category.incompatibleCategorys?.includes(selectedId)
    ) {
      incompatibleCategories.push(selectedId);
    }
  }

  return incompatibleCategories;
}

// Main function to determine categories to remove to make compatible
function categoriesToRemoveToMakeCompatible(
  subcatmap: SubCategoryMap,
  categoryId: string,
  categorys: CategoryData[],
  checkedIds: string[] = []
): string[] {
  if (isCompatibleCategory(subcatmap, categoryId, categorys, checkedIds)) {
    return [];
  }

  const removeCandidates = [
    ...findIncompatibleCategories(categoryId, subcatmap, categorys),
    ...findBlockingCategories(categoryId, subcatmap, categorys, checkedIds),
  ];
  return Array.from(new Set(removeCandidates));
}

// Helper function to find blocking categories for requirements recursively
function findBlockingCategories(
  categoryId: string,
  subcatmap: SubCategoryMap,
  categorys: CategoryData[],
  checkedIds: string[]
): string[] {
  const category = categorys.find((c) => c._id === categoryId);
  if (!category?.requiredCategorys || checkedIds.includes(categoryId)) {
    return [];
  }

  const blockingCategories: string[] = [];
  for (const requiredId of category.requiredCategorys) {
    const requiredCategory = categorys.find((c) => c._id === requiredId);
    if (!requiredCategory) continue;

    if (
      !isCompatibleCategory(subcatmap, requiredId, categorys, [
        ...checkedIds,
        categoryId,
      ])
    ) {
      const recursiveRemovals = categoriesToRemoveToMakeCompatible(
        subcatmap,
        requiredId,
        categorys,
        [...checkedIds, categoryId]
      );
      const selectedCategories = getSelectedCategories(subcatmap);
      const directBlocking: string[] = [];
      for (const selectedId of selectedCategories) {
        const selectedCategory = categorys.find((c) => c._id === selectedId);
        if (!selectedCategory) continue;
        if (
          selectedCategory.incompatibleCategorys?.includes(requiredId) ||
          requiredCategory.incompatibleCategorys?.includes(selectedId)
        ) {
          directBlocking.push(selectedId);
        }
      }
      blockingCategories.push(...recursiveRemovals, ...directBlocking);
    }
  }

  return blockingCategories;
}

export { categoriesToRemoveToMakeCompatible };
